import { V2, newSubmissionId } from "./responses";
import { evaluateResponse, stepCompletes } from "./shared/responseEvaluation";

// An attempt held only in memory, for sessions that must write nothing: the
// teacher's preview and My Learning's "Practise again" (Handoff 007 §8). It
// answers exactly like the gateway's submission route, using the same shared
// evaluator and policy rules, so the same screens behave the same way.
//
// When answers are withheld, nothing can be compared: a valid response is
// accepted and moves on, and no correctness is shown.

export class LocalSubmissionError extends Error {
  constructor(reason) {
    super(reason);
    this.reason = reason;
    this.response = { status: 409 };
  }
}

export const newLocalAttempt = (id = `local-${newSubmissionId()}`) => ({
  id,
  completed: false,
  currentStep: 0,
  responses: {},
  completedAt: null,
  source: "local",
});

// `activity` is an adapted activity (it carries its raw `definition`).
export const submitLocally = (activity, attempt, { stepId, submissionId, response }, policy) => {
  const steps = activity.definition.steps;
  const index = attempt.currentStep;
  const step = steps[index];
  if (attempt.completed) throw new LocalSubmissionError("completed");
  if (!step || String(step.id) !== stepId) throw new LocalSubmissionError("not-current-step");
  const previous = attempt.responses[stepId]?.submissions || [];
  if (policy.retryPolicy === "none" && previous.length) {
    throw new LocalSubmissionError("retry-not-allowed");
  }
  let outcome = null;
  if (!activity.answersWithheld) {
    const evaluated = evaluateResponse(step, response);
    if (!evaluated) throw new LocalSubmissionError("bad-response");
    outcome = evaluated.outcome;
  }
  const complete = outcome === null || stepCompletes(policy, outcome);
  const last = index === steps.length - 1;
  const record = {
    format: V2,
    submissions: [
      ...previous,
      { id: submissionId, response, outcome, submitted_at: new Date().toISOString() },
    ],
    current: response,
    state: outcome || "submitted",
  };
  const next = {
    ...attempt,
    responses: { ...attempt.responses, [stepId]: record },
    currentStep: complete && !last ? index + 1 : index,
    completed: complete && last,
    completedAt: complete && last ? new Date().toISOString() : null,
  };
  const reveal =
    policy.feedbackTiming === "immediate" ||
    (policy.feedbackTiming === "after_activity" && next.completed);
  return {
    attempt: next,
    outcome: reveal ? outcome : null,
    stepComplete: complete,
  };
};
